/**
 * Push tracker: the state machine plus its consequences — ops spent, the stored
 * reversal plan, and the reverse action.
 *
 * Used both inline (Push tab of the review screen) and on its own route
 * (`/pushes/$pushId`) so a push can be watched from anywhere.
 */
import { useState } from 'react'
import { usePush, useReversePush } from '@/api/hooks'
import type { PushStatusResponse } from '@/api/types'
import { formatDateTime, formatNumber, platformLabel } from '@/lib/format'
import { Badge, Button, Cap, ErrorNotice, KeyValue, Panel, Spinner, Textarea } from './ui'
import { PushStateLadder } from './PushStateLadder'

function retryAfterFrom(push: PushStatusResponse | undefined): string | null {
  if (!push) return null
  const messages = [
    ...(push.result?.errors ?? []).map((error) => error.message),
    ...(push.preflight?.blockers ?? []).map((blocker) => blocker.message),
  ].join(' ')
  const match = /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z)/.exec(messages)
  return match ? match[1]! : null
}

export function PushTracker({ pushId }: { pushId: string }) {
  const push = usePush(pushId)
  const reverse = useReversePush(pushId)
  const [confirming, setConfirming] = useState(false)
  const [reason, setReason] = useState('')

  if (push.isLoading) return <Spinner label="Reading push state…" />
  if (push.isError) return <ErrorNotice error={push.error} />
  const data = push.data
  if (!data) return null

  const retryAfter = retryAfterFrom(data)
  const canReverse = data.state === 'live_paused' || data.state === 'failed'
  const plan = data.reversal_plan
  const planSteps = plan?.steps ?? []

  return (
    <div className="flex flex-col gap-4">
      <Panel
        title={
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-[13px] font-semibold text-mist-100">Push state</h2>
            <Badge tone="neutral">{platformLabel(data.platform)}</Badge>
            <span className="num text-[11px] text-mist-500">{data.push_id}</span>
          </div>
        }
        actions={
          push.isFetching ? <span className="text-[11.5px] text-mist-500">polling…</span> : null
        }
        testId="push-tracker"
      >
        <PushStateLadder state={data.state} />

        {retryAfter ? (
          <div
            data-testid="retry-after"
            className="mt-3 rounded-md border border-signal-warn/40 bg-signal-warn/10 px-3 py-2 text-[12.5px] text-mist-200"
          >
            <Cap>Retry after</Cap>
            <div className="num mt-0.5 text-signal-warn">{retryAfter}</div>
            <p className="mt-1 text-mist-400">
              Operation quota is leased per day per platform, with a fair share per tenant.
              This push re-enters publishing as soon as a lease is granted.
            </p>
          </div>
        ) : null}

        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <KeyValue label="Ops estimated">
            <span className="num">{formatNumber(data.ops_estimated)}</span>
          </KeyValue>
          <KeyValue label="Ops spent">
            <span className="num">{formatNumber(data.ops_spent)}</span>
          </KeyValue>
          <KeyValue label="Requests">
            <span className="num">
              {data.result?.requests_made ?? 0}
              <span className="text-mist-500"> / {data.Estimate?.requests_required ?? 1}</span>
            </span>
          </KeyValue>
          <KeyValue label="Updated">
            <span className="num">{formatDateTime(data.updated_at)}</span>
          </KeyValue>
        </div>
      </Panel>

      {plan ? (
        <Panel
          title="Stored reversal plan"
          subtitle={`Every push produces one. ${planSteps.length} steps, children before parents — the only order both platforms accept.`}
          testId="reversal-plan"
        >
          <details className="group">
            <summary className="cursor-pointer list-none text-[12.5px] text-mist-300 hover:text-mist-100">
              <span className="group-open:hidden">Show all {planSteps.length} steps</span>
              <span className="hidden group-open:inline">Hide steps</span>
            </summary>
            <ol className="mt-2 max-h-72 overflow-auto rounded-md border border-ink-750">
              {planSteps.slice(0, 80).map((step, index) => (
                <li
                  key={`${step.entity}:${step.external_ref}:${index}`}
                  className="flex items-center gap-3 border-b border-ink-800 px-3 py-1.5 text-[12px] last:border-b-0"
                >
                  <span className="num w-10 text-mist-500">{index + 1}</span>
                  <Badge tone="neutral">{step.action}</Badge>
                  <span className="text-mist-300">{step.entity}</span>
                  <code className="num ml-auto text-mist-400">{step.external_ref}</code>
                </li>
              ))}
            </ol>
          </details>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            {canReverse ? (
              confirming ? (
                <div className="flex w-full flex-col gap-2">
                  <Textarea
                    aria-label="Reason for reversing"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    placeholder="Why is this being reversed? (recorded with the plan)"
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="danger"
                      data-testid="confirm-reverse"
                      disabled={reverse.isPending}
                      onClick={() =>
                        reverse.mutate(
                          { confirm: true, reason: reason.trim() === '' ? null : reason.trim() },
                          { onSuccess: () => setConfirming(false) },
                        )
                      }
                    >
                      {reverse.isPending ? 'Reversing…' : 'Yes — remove everything this push created'}
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirming(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button variant="danger" data-testid="reverse-push" onClick={() => setConfirming(true)}>
                  Reverse this push
                </Button>
              )
            ) : (
              <p className="text-[12px] text-mist-500">
                Reversal is available once the push is live (paused) or failed.
              </p>
            )}
            {reverse.isError ? <ErrorNotice error={reverse.error} /> : null}
          </div>
        </Panel>
      ) : null}
    </div>
  )
}
